import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarIcon, Plus, Trash2, Save, Lock } from "lucide-react";
import { toast } from "sonner";

const DAYS = [
  { key: "monday", label: "Segunda-feira" },
  { key: "tuesday", label: "Terça-feira" },
  { key: "wednesday", label: "Quarta-feira" },
  { key: "thursday", label: "Quinta-feira" },
  { key: "friday", label: "Sexta-feira" },
  { key: "saturday", label: "Sábado" },
  { key: "sunday", label: "Domingo" },
];

const PRESET_SLOTS = [
  "08:00","09:00","10:00","11:00","12:00",
  "13:00","14:00","15:00","16:00","17:00","18:00","19:00",
];

export const AvailabilityManager = () => {
  const [schedule, setSchedule] = useState(() => Object.fromEntries(DAYS.map((d) => [d.key, []])));
  const [blocked, setBlocked] = useState([]);
  const [blockDate, setBlockDate] = useState("");
  const [blockReason, setBlockReason] = useState("");

  const load = async () => {
    const [s, b] = await Promise.all([api.get("/availability"), api.get("/availability/blocked")]);
    setSchedule(s.data);
    setBlocked(b.data);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const toggle = (day, slot) => {
    const cur = schedule[day] || [];
    const next = cur.includes(slot) ? cur.filter((s) => s !== slot) : [...cur, slot].sort();
    setSchedule({ ...schedule, [day]: next });
  };

  const save = async () => {
    try { await api.put("/availability", schedule); toast.success("Agenda semanal salva"); }
    catch { toast.error("Erro ao salvar"); }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Restaurar horários padrão (Seg-Sex 08-12h / 14-18h, Sáb 08-12h)?")) return;
    const def = {
      monday:    ["08:00","09:00","10:00","11:00","14:00","15:00","16:00","17:00"],
      tuesday:   ["08:00","09:00","10:00","11:00","14:00","15:00","16:00","17:00"],
      wednesday: ["08:00","09:00","10:00","11:00","14:00","15:00","16:00","17:00"],
      thursday:  ["08:00","09:00","10:00","11:00","14:00","15:00","16:00","17:00"],
      friday:    ["08:00","09:00","10:00","11:00","14:00","15:00","16:00","17:00"],
      saturday:  ["08:00","09:00","10:00","11:00"],
      sunday:    [],
    };
    try { await api.put("/availability", def); setSchedule(def); toast.success("Padrão restaurado"); }
    catch { toast.error("Erro ao restaurar"); }
  };

  const addBlock = async () => {
    if (!blockDate) { toast.error("Escolha uma data"); return; }
    try {
      await api.post("/availability/blocked", { date: blockDate, reason: blockReason });
      toast.success("Data bloqueada");
      setBlockDate(""); setBlockReason("");
      await load();
    } catch { toast.error("Erro"); }
  };

  const removeBlock = async (d) => {
    try { await api.delete(`/availability/blocked/${d}`); toast.success("Desbloqueada"); await load(); }
    catch { toast.error("Erro"); }
  };

  return (
    <div className="space-y-6">
      {/* Weekly schedule */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="flex items-center gap-2 text-sky-400">
                <CalendarIcon className="w-5 h-5" />
                <h2 className="text-lg font-bold text-white">Agenda semanal</h2>
              </div>
              <p className="text-xs text-slate-400 mt-1">Marque os horários que você atende em cada dia. Os clientes só poderão escolher esses horários.</p>
            </div>
            <Button onClick={save} className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold" data-testid="save-availability-btn">
              <Save className="w-4 h-4 mr-1" /> Salvar agenda
            </Button>
            <Button onClick={resetDefaults} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800 ml-2" data-testid="reset-availability-btn">
              Restaurar padrão
            </Button>
          </div>

          <div className="space-y-4">
            {DAYS.map((d) => {
              const selected = schedule[d.key] || [];
              return (
                <div key={d.key} className="border border-slate-800 rounded-lg p-4" data-testid={`day-${d.key}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-white">{d.label}</h3>
                      <span className="text-xs text-slate-400">{selected.length === 0 ? "Fechado" : `${selected.length} horário(s)`}</span>
                    </div>
                    <div className="flex gap-1.5">
                      <Button type="button" size="sm" variant="ghost" onClick={() => setSchedule({ ...schedule, [d.key]: [...PRESET_SLOTS] })} className="text-xs text-sky-400 hover:bg-sky-500/10 h-7">
                        Todos
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setSchedule({ ...schedule, [d.key]: [] })} className="text-xs text-slate-400 hover:bg-slate-800 h-7">
                        Limpar
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-1.5">
                    {PRESET_SLOTS.map((s) => {
                      const on = selected.includes(s);
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => toggle(d.key, s)}
                          data-testid={`slot-${d.key}-${s}`}
                          className={`py-2 text-xs rounded font-mono transition-colors ${on ? "bg-sky-500 text-slate-950 font-bold" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}
                        >{s}</button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Blocked dates */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-sky-400 mb-1">
            <Lock className="w-5 h-5" />
            <h2 className="text-lg font-bold text-white">Datas bloqueadas (feriados, folgas)</h2>
          </div>
          <p className="text-xs text-slate-400 mb-5">Bloqueie dias específicos — nenhum horário aparecerá para o cliente nessas datas.</p>

          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] mb-5">
            <div>
              <Label className="text-slate-300 text-xs">Data</Label>
              <Input type="date" value={blockDate} onChange={(e) => setBlockDate(e.target.value)} className="bg-slate-800 border-slate-700 text-white mt-1 h-10" data-testid="block-date-input" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Motivo (opcional)</Label>
              <Input value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder="Feriado nacional" className="bg-slate-800 border-slate-700 text-white mt-1 h-10" data-testid="block-reason-input" />
            </div>
            <div className="flex items-end">
              <Button onClick={addBlock} className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold h-10" data-testid="add-block-btn">
                <Plus className="w-4 h-4 mr-1" /> Bloquear
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            {blocked.length === 0 && <div className="text-sm text-slate-500 py-3">Nenhuma data bloqueada.</div>}
            {blocked.map((b) => (
              <div key={b.date} className="flex items-center justify-between bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2.5" data-testid={`blocked-${b.date}`}>
                <div>
                  <span className="text-white font-mono font-bold">{b.date.split("-").reverse().join("/")}</span>
                  {b.reason && <Badge className="ml-3 bg-slate-700 text-slate-300 border-slate-600">{b.reason}</Badge>}
                </div>
                <Button size="icon" variant="ghost" onClick={() => removeBlock(b.date)} className="text-red-400 hover:bg-red-500/10 h-8 w-8">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
